import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, AutoComplete, Skeleton, Tooltip } from 'antd';
import GreyX from '@assets/svgs/grey-x-icon.svg';
import BlackPlus from '@assets/svgs/black-plus-icon.svg';
import { deleteTag, postTag } from '@services/applicants/applicants-http.service';
import useNotification from '@hooks/notification/useNotification';
import StateEnum from '@constants/state.enum';
import { getTags } from '@services/company/company-http.service';
import { useTranslation } from 'next-i18next';
import ErrorShield from '@assets/svgs/error-shield.svg';
import styles from './ApplicantTags.module.scss';

type ApplicantTagsProps = {
  applicantId: number;
  tags: Array<{ id: number; title: string }>;
};

const ApplicantTags: React.FC<ApplicantTagsProps> = ({ applicantId, tags }) => {
  const { t: translate } = useTranslation('applicant');
  const [form] = Form.useForm();
  const [loadTags, setLoadTags] = useState<boolean>(false);
  const [newTagState, setNewTagState] = useState<'edit' | 'view'>('view');
  const [activeTags, setActiveTags] = useState<Array<{ id: number; title: string }>>([]);
  const [applicantTags, setApplicantTags] = useState<Array<{ id: number; title: string }>>(tags);
  const [searchText, setSearchText] = useState<string>('');
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [warningCountdown, setWarningCountdown] = useState<number>(6);

  const inputRef = useRef<any>(null);

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );

  const deleteTagFunc = async (tagId: string) => {
    setLoadTags(true);
    try {
      const updatetags = await deleteTag(applicantId.toString(), tagId);
      await setApplicantTags(updatetags);
      setLoadTags(false);
    } catch (e: any) {
      generalError();
      setLoadTags(false);
    }
  };

  const fetchTags = useCallback(
    async (tagTitle: string) => {
      try {
        const fetchActiveTags = await getTags(tagTitle);
        await setActiveTags([{ id: -1, title: searchText }, ...fetchActiveTags]);
      } catch (e: any) {
        generalError();
      }
    },
    [searchText, activeTags, applicantTags],
  );

  useEffect(() => {
    if (searchText.length >= 3) {
      fetchTags(searchText);
    } else {
      setActiveTags([{ id: -1, title: searchText }]);
    }
  }, [searchText]);

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const addNewTagOnCandidate = async (tagElement: { id: number; value: string }) => {
    setLoadTags(true);
    try {
      const updatedApplicants = await postTag(applicantId.toString(), tagElement.value);
      await setApplicantTags(updatedApplicants);
      await setLoadTags(false);
      await form.resetFields();
      await setNewTagState('edit');
      await focusInput();
    } catch (e: any) {
      generalError();
      setLoadTags(false);
    }
  };

  const renderViewNewTag = () => {
    return (
      <div
        role="button"
        className={` ${styles.wrapNewTag}`}
        tabIndex={0}
        onClick={async () => {
          await setNewTagState('edit');
          await focusInput();
        }}
        onKeyDown={() => setNewTagState('edit')}
      >
        <div className={styles.newTagIcon}>
          <BlackPlus data-testid="add-tag-icon" />
        </div>
        <div className={`main-body-text ${styles.newTagText}`}>{translate('new_tag')}</div>
      </div>
    );
  };

  const handleEnterTrigger = (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      addNewTagOnCandidate({ id: -1, value: event.target.value });
      setShowWarning(false);
    }
  };

  const tooltipContent = (
    <div className={styles.tooltipContent}>
      <ErrorShield className={styles.errorShield} />
      {warningCountdown}
      {translate('remaining_characters')}
    </div>
  );

  const renderAddNewTag = () => {
    return (
      <Form
        name="addTag"
        layout="inline"
        className="antd-overwrite"
        form={form}
        onBlur={() => {
          setNewTagState('view');
          setActiveTags([]);
          setShowWarning(false);
          form.resetFields();
        }}
      >
        <Form.Item name="tag">
          <Tooltip title={tooltipContent} open={showWarning}>
            <AutoComplete
              showSearch
              autoFocus
              ref={inputRef}
              className={styles.selectInput}
              dropdownStyle={{}}
              maxLength={30}
              popupClassName="selectInput"
              onSearch={(value) => {
                const trimmedValue = value.trimStart();
                setSearchText(trimmedValue);
                setWarningCountdown(30 - trimmedValue.length);
                setShowWarning(trimmedValue.length >= 24);
              }}
              onSelect={(id: number, value: { id: number; value: string }) => addNewTagOnCandidate(value)}
              onKeyDown={handleEnterTrigger}
              suffixIcon={false}
              defaultActiveFirstOption
              notFoundContent={false}
              data-testid="autocomplete-input"
              options={
                activeTags &&
                activeTags
                  .filter((d, index, self) => {
                    return index === self.findIndex((t) => t.title === d.title);
                  })
                  .map((d) => ({
                    id: d.id,
                    value: d.title,
                  }))
              }
            />
          </Tooltip>
        </Form.Item>
      </Form>
    );
  };

  return loadTags ? (
    <Skeleton paragraph={{ rows: 0 }} active />
  ) : (
    <div className={styles.wrapTags}>
      {applicantTags
        ?.filter((tag, index, self) => self.findIndex((t) => t.title === tag.title) === index)
        .map(
          (tag) =>
            tag.title && (
              <div key={tag.id} className={`${styles.wrapTag}`}>
                <div className={`main-body-text ${styles.tagText}`}>{tag.title}</div>
                <div
                  role="button"
                  tabIndex={0}
                  className={styles.tagIcon}
                  onClick={() => deleteTagFunc(tag.id.toString())}
                  onKeyDown={() => deleteTagFunc(tag.id.toString())}
                >
                  <GreyX data-testid="delete-tag-icon" />
                </div>
              </div>
            ),
        )}
      <>{newTagState === 'view' ? renderViewNewTag() : renderAddNewTag()}</>
    </div>
  );
};
export default ApplicantTags;
